import { DownloadOutlined } from "@ant-design/icons";
import { Alert, Descriptions, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { AdminDeposit, RequestStatus } from "@/entities/deposit";
import { useDeposits } from "@/entities/deposit";
import { DepositActions } from "@/features/process-deposit";
import { errorMessage } from "@/shared/api";
import { SECTIONS } from "@/shared/config";
import { formatDateTime } from "@/shared/lib";
import {
  EmptyState,
  LoadMore,
  Money,
  PageHeader,
  SectionCard,
  StatusTag,
  UserCell,
} from "@/shared/ui";
import { StatusFilter } from "@/widgets/request-table";

const STATUSES: RequestStatus[] = ["pending", "approved", "rejected"];

/**
 * Платёж шлюза — раскрываемой строкой, а не колонками.
 *
 * Полей семь, и в таблице, которая уже не помещается в ноутбук, они бы
 * вытеснили то, по чему оператор принимает решение. При этом нужны они не
 * всегда, а ровно в двух случаях: разбор недоплаты и поиск платежа в кабинете
 * провайдера.
 *
 * Недоплата и переплата подписаны явным ярлыком, а не оставлены на сравнение
 * двух чисел глазами: у крипто-сумм по восемнадцать знаков, и «меньше ли
 * 99.98 чем 100.00» на такой строке читается не с первого взгляда.
 */
function PaymentPanel({ row }: { row: AdminDeposit }) {
  const { t } = useTranslation();
  const payment = row.payment;

  if (!payment) {
    return <Typography.Text type="secondary">{t("deposits.payment.none")}</Typography.Text>;
  }

  // Сравнение через Number — не арифметика над деньгами, а выбор ярлыка:
  // результат идёт в подпись, а не в проводку.
  const expected = Number(payment.pay_amount);
  const paid = Number(payment.actually_paid);
  const currency = payment.pay_currency.toUpperCase();

  return (
    <Descriptions column={2} size="small" bordered title={t("deposits.payment.title")}>
      <Descriptions.Item label={t("deposits.payment.id")}>
        <Typography.Text copyable code>
          {payment.payment_id}
        </Typography.Text>
      </Descriptions.Item>
      <Descriptions.Item label={t("deposits.payment.status")}>
        <Tag color={payment.credited_at ? "green" : "default"}>{payment.payment_status}</Tag>
      </Descriptions.Item>
      <Descriptions.Item label={t("deposits.payment.expected")}>
        {payment.pay_amount} {currency}
      </Descriptions.Item>
      <Descriptions.Item label={t("deposits.payment.received")}>
        {payment.actually_paid} {currency}{" "}
        {paid < expected && <Tag color="orange">{t("deposits.payment.underpaid")}</Tag>}
        {paid > expected && <Tag color="blue">{t("deposits.payment.overpaid")}</Tag>}
      </Descriptions.Item>
      {payment.parent_payment_id && (
        <Descriptions.Item label={t("deposits.payment.parent")}>
          <Typography.Text copyable code>
            {payment.parent_payment_id}
          </Typography.Text>
        </Descriptions.Item>
      )}
      {payment.expires_at && (
        <Descriptions.Item label={t("deposits.payment.expires")}>
          {formatDateTime(payment.expires_at)}
        </Descriptions.Item>
      )}
    </Descriptions>
  );
}

export function DepositsPage() {
  const { t } = useTranslation();
  const [statuses, setStatuses] = useState<RequestStatus[]>(["pending"]);
  const query = useDeposits(statuses);

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  /**
   * Колонок семь, а не девять: способ со связкой «актив · сеть» и статус с
   * датой съехались попарно. Порознь после укрупнения шрифта таблица не
   * помещалась в ноутбук, и закреплённые справа действия наезжали на статус —
   * то есть прятали ровно то, по чему оператор решает, жать ли кнопку.
   *
   * Порядок не случаен: статус стоит сразу за суммой, до реквизитов. На
   * ноутбуке таблица не помещается целиком, и уезжать вправо должны справочные
   * поля — хеш и реквизиты, — а не то, по чему оператор решает, жать ли кнопку.
   */
  const columns: ColumnsType<AdminDeposit> = [
    { title: t("common.id"), dataIndex: "id", width: 84 },
    {
      title: t("common.user"),
      key: "user",
      render: (_, row) => (
        <UserCell
          id={row.user.id}
          username={row.user.username}
          firstName={row.user.first_name}
          telegramId={row.user.telegram_id}
        />
      ),
    },
    {
      title: t("common.amount"),
      key: "amount",
      align: "right",
      width: 170,
      render: (_, row) => <Money value={row.amount} />,
    },
    {
      title: t("common.status"),
      key: "status",
      width: 210,
      render: (_, row) => (
        <Space orientation="vertical" size={4} align="start">
          <StatusTag status={row.status} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {formatDateTime(row.created_at)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: t("common.requisites"),
      key: "requisites",
      width: 180,
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <span>{row.method}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {[row.asset, row.network].filter(Boolean).join(" · ") || "—"}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: t("deposits.txHash"),
      key: "tx_hash",
      width: 220,
      // Хеш обрезается по ширине, но копируется целиком: оператор сверяет его
      // в блокчейн-эксплорере, и обрезанная строка там бесполезна.
      render: (_, row) =>
        row.tx_hash ? (
          <Typography.Text copyable ellipsis style={{ maxWidth: 170 }}>
            {row.tx_hash}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">{t("deposits.noHash")}</Typography.Text>
        ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      // Колонка НЕ закреплена справа. Закреплённая всегда лежит поверх
      // прокручиваемой части, и на ноутбуке 1366 она наползала на статус —
      // прятала ровно то, по чему принимают решение. Пусть лучше строка
      // едет целиком: горизонтальная прокрутка у широкой таблицы привычна,
      // а перекрытие данных кнопками — нет.
      width: 380,
      render: (_, row) => (
        <DepositActions
          id={row.id}
          userId={row.user.id}
          amount={row.amount}
          disabled={row.status !== "pending"}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        section="deposits"
        title={t("deposits.title")}
        subtitle={t("deposits.subtitle")}
        extra={<StatusFilter value={statuses} onChange={setStatuses} statuses={STATUSES} />}
      />

      {query.isError && (
        <Alert type="error" showIcon title={errorMessage(query.error)} style={{ marginBottom: 16 }} />
      )}

      <SectionCard color={SECTIONS.deposits.color} bodyPadding={0}>
        <Table<AdminDeposit>
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={query.isPending}
          pagination={false}
          scroll={{ x: 1520 }}
          expandable={{
            expandedRowRender: (row) => <PaymentPanel row={row} />,
            // Раскрывать нечего у заявок, заведённых до перехода на шлюз, —
            // но стрелка у них остаётся: её отсутствие читалось бы как
            // «данные не загрузились», а не как «платежа нет».
            rowExpandable: () => true,
          }}
          locale={{
            emptyText: <EmptyState icon={<DownloadOutlined aria-hidden />} title={t("deposits.empty")} />,
          }}
        />
        <LoadMore
          hasMore={query.hasNextPage}
          loading={query.isFetchingNextPage}
          onClick={() => {
            void query.fetchNextPage();
          }}
        />
      </SectionCard>
    </>
  );
}
